import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import PrimaryButton from '../../../components/shacdn/PrimaryButton';
import Spline from '@splinetool/react-spline';
const BannerParallax: React.FC = () => {
  const [mainVisible, setMainVisible] = useState(true);
  const [tempDivRatio, setTempDivRatio] = useState(0); // intersection ratio
  const tempDivRef = useRef<HTMLDivElement>(null);
  const [shouldRenderSpline, setShouldRenderSpline] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRenderSpline(true);
    }, 100); // Adjust delay as needed
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setMainVisible(entry.isIntersecting);
        setTempDivRatio(entry.intersectionRatio);
      },
      { threshold: Array.from({ length: 101 }, (_, i) => i / 100) }
    );
    if (tempDivRef.current) {
      observer.observe(tempDivRef.current);
    }
    return () => {
      if (tempDivRef.current) observer.unobserve(tempDivRef.current);
    };
  }, []);

  // Opacity: 0.3 (0% in view) -> 1 (100% in view)
  const mainDivOpacity = 0.1 + 0.9 * tempDivRatio;
  const mainDivScale = 0.7 + 0.3 * tempDivRatio;

  return (
    <>
      <div
        className={`fixed top-0 main-div left-0 w-full min-h-[49.4vw] bg-cover bg-center flex flex-col items-center justify-center z-0 transition-opacity duration-500 ${mainVisible ? '' : 'opacity-0 invisible'}`}
        style={{
          opacity: mainDivOpacity
        }}
      >
        <div className='absolute inset-0 bg-gradient-to-b from-white/80 to-white/0 z-10' />
        {shouldRenderSpline && (
          <div className='relative z-[1] w-full min-h-[700px] flex flex-col items-center justify-center'>
            <div className='w-full min-w-[800px]'>
              <Spline scene='https://prod.spline.design/FRxny7An0TLdTiiX/scene.splinecode' />
            </div>
          </div>
        )}
        <div className='absolute top-[50%] -translate-x-1/2 -translate-y-1/2 left-[50%] z-20 flex flex-col items-center justify-center h-full w-full gap-12'>
          <div
            style={{ transform: `scale(${mainDivScale})` }}
            className=' flex flex-col items-center justify-center gap-12'
          >
            <motion.h1
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='text-6xl max-[768px]:text-4xl max-[560px]:text-3xl font-bold text-center max-w-[1294px] text-[#100E12]'
            >
              Exceptional Digital Experiences and Marketing Solutions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className='text-xl px-4 max-[560px]:text-base text-center max-w-[1050px] text-gray-700'
            >
              We help ambitious businesses scale with powerful software, captivating design, and smart marketing.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className='flex gap-4'
            >
              <PrimaryButton>Our projects</PrimaryButton>
              <PrimaryButton variant='secondary'>Work with us</PrimaryButton>
            </motion.div>
          </div>
        </div>
      </div>
      <div
        ref={tempDivRef}
        className='opacity-0 temp-div mt-[-77px] min-h-[700px] invisible w-full h-[49.4vw] bg-cover bg-center flex flex-col items-center justify-center z-0'
      >
        <div className='absolute inset-0 bg-gradient-to-b from-white/80 to-white/0 z-10' />
        <div className='relative z-20 flex flex-col items-center justify-center h-full w-full gap-12'>
          <motion.h1
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='text-6xl max-[768px]:text-xl font-bold text-center max-w-3xl text-[#100E12]'
          >
            Exceptional Digital Experiences and Marketing Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className='text-lg md:text-xl text-center max-w-xl text-gray-700'
          >
            We help ambitious businesses scale with powerful software, captivating design, and smart marketing.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className='flex gap-4'
          >
            <PrimaryButton>Our projects</PrimaryButton>
            <PrimaryButton variant='secondary'>Work with us</PrimaryButton>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default BannerParallax;
